import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '../../lib/utils';

/**
 * A panel anchored to a trigger, rendered in a portal.
 *
 * The portal is the whole point. The composer's publish rail is `overflow-y-auto`
 * inside an `overflow-hidden` workspace, so an absolutely-positioned panel is
 * clipped by two ancestors before it ever reaches the viewport edge — and no
 * z-index fixes that, because clipping is not a stacking question. Radix renders
 * the content at the document root and positions it against the trigger's
 * measured rect instead.
 *
 * `collisionPadding` is the second half: content anchored inside a right-hand
 * rail has nowhere to open when the window sits against the right of the screen,
 * so it flips its alignment rather than running off the edge.
 */

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'start', sideOffset = 6, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={8}
      className={cn(
        'z-50 rounded-xl border border-slate-200 bg-white p-3 shadow-lg shadow-slate-900/10 outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
